import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import AuthBar from './app/components/AuthBar';
import { LoLAContextProvider } from '@nicherest/lola/training/LoLAContext';
import { ThemeProvider } from './app/modules/businesses/themes/craft-fair-vendor/components/ThemeContext';
import VendorBusinessPage from './app/modules/businesses/themes/craft-fair-vendor/templates/CraftFairVendorPage';
import { CartDrawer } from './app/components/CartDrawer';

class VendorErrorBoundary extends React.Component<
  { slug: string; children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[nicherest] Vendor page render error:', error.message, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ maxWidth: 700, margin: '60px auto', padding: 24, background: '#fee2e2', border: '2px solid #ef4444', borderRadius: 8, color: '#991b1b', fontFamily: 'monospace', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
          <strong>Vendor page render error ({this.props.slug}):</strong>{'\n'}{this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

const slug: string | null = (window as any).__APP_SLUG__ || null;
const data: any = (window as any).__INITIAL_DATA__ || null;
const isSSR: boolean = (window as any).__SSR__ === true;

const rootEl = document.getElementById('root') as HTMLElement;

if (isSSR && slug) {
  // Mount AuthBar in its own root (no SSR — avoids hydration mismatch with vendor page HTML).
  const authRootEl = document.getElementById('auth-root');
  if (authRootEl) {
    ReactDOM.createRoot(authRootEl).render(
      <AuthProvider>
        <CartProvider>
          <AuthBar />
        </CartProvider>
      </AuthProvider>
    );
  }

  // Hydrate the SSR'd vendor page. Tree must match ssrEntry.tsx renderBusinessPage exactly.
  ReactDOM.hydrateRoot(
    rootEl,
    <VendorErrorBoundary slug={slug}>
      <BrowserRouter>
        <LoLAContextProvider>
          <CartProvider>
            <CartDrawer />
            <ThemeProvider initialData={data}>
              <VendorBusinessPage businessName={slug} geo={data?.geo} />
            </ThemeProvider>
          </CartProvider>
        </LoLAContextProvider>
      </BrowserRouter>
    </VendorErrorBoundary>
  );
} else {
  // Non-SSR paths: CRA dev server, /cart, /checkout.
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

reportWebVitals();
